import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import debounce from 'lodash/debounce';
import { FormattedMessage } from '../../../../util/reactIntl';
import { saveRentalDefaults, pickRentalDefaultsFromValues } from '../../../../util/rentalDefaults';

import RentalCard from '../RentalCard/RentalCard';
import NoResult from '../NoResult/NoResult';
import { H1, H4, SkeletonCard } from '../../../../components';
import { useListingAvailability } from '../../../../hooks/useListingAvailability';
import { getItemOperationKey } from '../../../../ducks/cart.duck';
import {
  saveVisibleCount,
  getSavedVisibleCount,
  getRestorationKey,
} from '../../../../util/scrollRestoration';
import {
  buildGroupedSections,
  flattenSectionsInDisplayOrder,
  rebuildSectionsFromVisibleHits,
} from './categoryGrouping';
import css from './CustomHits.module.css';

const INITIAL_VISIBLE_COUNT = 12;
const VISIBLE_INCREMENT = 12;
const INITIAL_SKELETON_COUNT = 12;
// Upper bound on skeleton cards so a restored deep-scroll visibleCount (see
// scrollRestoration.js) can't blow up the DOM before real data even arrives.
const MAX_SKELETON_COUNT = 60;

const StoreSkeletonGrid = ({ className, count = INITIAL_SKELETON_COUNT }) => (
  <div className={css.skeletonWrapper}>
    <div className={className}>
      {Array.from({ length: count }, (_, index) => (
        <SkeletonCard key={`skeleton-${index}`} className={css.skeletonCard} />
      ))}
    </div>
  </div>
);


const CustomHits = props => {
  const {
    intl,
    sortOption,
    storeUserName,
    totalListings,
    config,
    onAddToCart,
    onUpdateCart,
    isCurrentUser,
    exchangeRates,
    currentUserCurrency,
    routes,
    history,
    dateRangeFilter,
    params,
    storeTimeZone,
    filteredCartItems = [],
    viewMode = 'list',
    dateRangePickerRef,
    datePickerAnchorRef,
    storePath,
    user,
    selectedCategoryFilter,
    // onCategoryChange,
    searchQuery,
    pendingSearchSettledScrollRef,
    onScrollToSearchSection,
    onScrollCategoryChange,
  } = props;

  // const { locale } = React.useContext(LanguageContext);

  const isQueryMode = Boolean(searchQuery && searchQuery.trim().length > 0);

  // Redux hooks
  const { status, error } = useInstantSearch({ catchError: true });
  const { items } = useInfiniteHits();

  useEffect(() => {
    if (status !== 'idle') return;
    if (!pendingSearchSettledScrollRef?.current) return;
    if (typeof onScrollToSearchSection !== 'function') return;
    pendingSearchSettledScrollRef.current = false;
    requestAnimationFrame(() => {
      onScrollToSearchSection();
    });
  }, [status, pendingSearchSettledScrollRef, onScrollToSearchSection]);
  

  // Redux state
  const { addToCartInProgress, itemOperationsInProgress = {} } = useSelector(
    state => state.cart
  );

  // Quantity state - per listing (keyed by listing.objectID)
  const [quantities, setQuantities] = useState({});

  // Track added items for success state
  const [addedItems, setAddedItems] = useState([]);

  // Memoize sorted items to prevent unnecessary re-sorting
  const renderableItems = useMemo(() => {
    if (!items || !items.length) return [];

    // Browse mode: preserve owner-defined order.
    // Query mode: preserve Algolia relevance ordering.
    if (!isQueryMode && sortOption === 'order') {
      // Create a new array to avoid mutating the original
      return [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    return items;
  }, [items, items.length, sortOption, isQueryMode]);

  const sections = useMemo(
    () =>
      isQueryMode
        ? [
            {
              sectionLabel: 'Results',
              hits: renderableItems,
              hideLabel: true,
            },
          ]
        : buildGroupedSections(renderableItems, {
            user,
            selectedCategoryFilter,
            locale: intl?.locale || 'en',
          }),
    [
      renderableItems,
      user,
      selectedCategoryFilter?.categoryId,
      selectedCategoryFilter?.depth,
      intl?.locale,
      isQueryMode,
    ]
  );

  const flattenedOrderedHits = useMemo(
    () => flattenSectionsInDisplayOrder(sections),
    [sections]
  );

  // Extract ownerId from params or first item
  const ownerId = params?.id;
  // Keyed by the current history entry so a restored navigation (browser back) can pick
  // up how many cards were revealed before leaving, instead of restarting at the first
  // page of results. See src/util/scrollRestoration.js.
  const locationKey = getRestorationKey(history?.location);
  const [visibleCount, setVisibleCount] = useState(() => {
    const saved = getSavedVisibleCount(locationKey);
    return saved && saved > INITIAL_VISIBLE_COUNT ? saved : INITIAL_VISIBLE_COUNT;
  });
  const hasMoreVisible = visibleCount < flattenedOrderedHits.length;
  const visibleHits = useMemo(
    () => flattenedOrderedHits.slice(0, visibleCount),
    [flattenedOrderedHits, visibleCount]
  );
  const visibleSections = useMemo(
    () => rebuildSectionsFromVisibleHits(sections, visibleHits),
    [sections, visibleHits]
  );

  // Total hit count per section from the FULL sections array (not the progressive subset).
  // Used for the count badge so it always shows the real total, not the rendered count.
  const sectionTotalCounts = useMemo(() => {
    const map = new Map();
    sections.forEach(section => {
      map.set(section.sectionPath || section.sectionLabel, section.hits.length);
    });
    return map;
  }, [sections]);

  const sentinelRef = useRef(null);

  // Refs to each rendered section element for IntersectionObserver tracking
  const sectionElsRef = useRef(new Map());

  // Which top-level category is currently in the viewport
  const [activeJumpKey, setActiveJumpKey] = useState(null);

  // Derive jump strip items from the full sections list (not just visible ones) so
  // the strip shows all available top-level categories from the start.
  const jumpItems = useMemo(() => {
    if (isQueryMode || !sections.length) return [];
    const seen = new Set();
    const items = [];
    sections.forEach(section => {
      if (section.hideLabel) return;
      const key = section.ancestors?.[0] || section.sectionLabel;
      if (key && !seen.has(key)) {
        seen.add(key);
        items.push({ key, firstSectionPath: section.sectionPath || section.sectionLabel });
      }
    });
    return items;
  }, [sections, isQueryMode]);

  // Reset progressive window when the listing set changes (store, keyword search, category).
  // Date range is intentionally excluded: it only changes availability overlays client-side,
  // not the set of listings returned from Algolia, so the user's scroll position is preserved.
  // Skipped on the very first run: visibleCount is already correctly initialized above
  // (either the default or a restored count), and this effect fires on mount too - without
  // the guard it would immediately stomp a restored count back down to the default.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [
    ownerId,
    searchQuery,
    selectedCategoryFilter?.categoryId,
    selectedCategoryFilter?.depth,
  ]);

  // Keep the saved count fresh as the user scrolls/loads more, so a later browser-back
  // to this history entry restores roughly where they left off.
  useEffect(() => {
    saveVisibleCount(locationKey, visibleCount);
  }, [locationKey, visibleCount]);

  // IntersectionObserver: reveal more cards from already-fetched hits.
  useEffect(() => {
    if (!hasMoreVisible || flattenedOrderedHits.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        if (!entries[0]?.isIntersecting) return;
        setVisibleCount(prev =>
          Math.min(prev + VISIBLE_INCREMENT, flattenedOrderedHits.length)
        );
      },
      { rootMargin: '200px 0px', threshold: 0.1 }
    );

    const el = sentinelRef.current;
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [hasMoreVisible, flattenedOrderedHits.length]);

  // IntersectionObserver: track which top-level category is in view to highlight
  // the active jump strip item. The root margin creates a narrow "scanning band"
  // near the top of the viewport — whichever section enters that band becomes active.
  useEffect(() => {
    if (!jumpItems.length) return;

    // Persistent set of sections currently inside the detection band.
    // entries only contains changed elements, so we track all intersecting ones here.
    const intersectingEls = new Set();

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) intersectingEls.add(e.target);
          else intersectingEls.delete(e.target);
        });

        if (!intersectingEls.size) return;

        // Among all currently-intersecting sections, pick the one whose top is
        // closest to (but not above) the detection zone — i.e. the section the
        // user has most recently scrolled into. Prefer sections with a positive top
        // (header still on-screen); fall back to the least-negative top.
        let activeEl = null;
        let activeTop = null;
        intersectingEls.forEach(el => {
          const top = el.getBoundingClientRect().top;
          if (activeTop === null) {
            activeEl = el;
            activeTop = top;
          } else if (top >= 0 && (activeTop < 0 || top < activeTop)) {
            activeEl = el;
            activeTop = top;
          } else if (activeTop < 0 && top > activeTop) {
            activeEl = el;
            activeTop = top;
          }
        });

        if (activeEl) setActiveJumpKey(activeEl.dataset.jumpKey || null);
      },
      // Detection band: 5% to 55% from the top of the viewport.
      // Fires as soon as the section header enters the upper half of the screen.
      { rootMargin: '-5% 0px -45% 0px', threshold: 0 }
    );

    sectionElsRef.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      intersectingEls.clear();
    };
  // Re-attach whenever new sections are rendered (progressive reveal adds more)
  }, [visibleSections.length, jumpItems.length]);

  // Notify parent which top-level category is in view so CategoryPicker can highlight it.
  useEffect(() => {
    onScrollCategoryChange?.(activeJumpKey);
  }, [activeJumpKey, onScrollCategoryChange]);

  // Use custom hook for availability management
  const { availabilityStatus, availabilityLoading } = useListingAvailability(
    visibleHits,
    dateRangeFilter,
    ownerId
  );

  // Helper function to find cart item for a listing
  const findCartItemForListing = useCallback(
    (listingId, dateRangeFilter, filteredCartItems) => {
      if (
        !dateRangeFilter?.startDate ||
        !dateRangeFilter?.endDate ||
        !filteredCartItems?.length
      ) {
        return null;
      }

      return filteredCartItems.find(cartItem => {
        const cartListingId = cartItem.listing?.id?.uuid || cartItem.listingId;
        const matchesListing = cartListingId === listingId;

        if (!matchesListing) return false;

        // Compared as calendar days in the STORE's zone (both are store-zone
        // midnights) - same rule as StoreProfilePage's filteredCartItems.
        const toDay = d => (storeTimeZone ? moment(d).tz(storeTimeZone) : moment(d));
        return (
          toDay(cartItem.bookingDates?.startDate).isSame(toDay(dateRangeFilter.startDate), 'day') &&
          toDay(cartItem.bookingDates?.endDate).isSame(toDay(dateRangeFilter.endDate), 'day')
        );
      });
    },
    [storeTimeZone]
  );

  // Initialize quantities for new items and sync with cart items
  useEffect(() => {
    setQuantities(prev => {
      const newQuantities = { ...prev };
      visibleHits.forEach(item => {
        const cartItem = findCartItemForListing(
          item.objectID,
          dateRangeFilter,
          filteredCartItems
        );
        if (cartItem) {
          // Initialize from cart item if it exists
          newQuantities[item.objectID] = cartItem.quantity || 1;
        } else if (!newQuantities[item.objectID]) {
          // Default to 1 if not in cart and not already set
          newQuantities[item.objectID] = 1;
        }
      });
      // Remove quantities for items no longer visible
      Object.keys(newQuantities).forEach(id => {
        if (!visibleHits.find(item => item.objectID === id)) {
          delete newQuantities[id];
        }
      });
      return newQuantities;
    });
  }, [
    visibleHits,
    dateRangeFilter,
    filteredCartItems,
    findCartItemForListing,
  ]);

  // Reset quantities when dates are cleared
  useEffect(() => {
    if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
      setQuantities(prev => {
        const reset = {};
        Object.keys(prev).forEach(id => {
          reset[id] = 1; // Reset to 1, don't remove
        });
        return reset;
      });
    }
  }, [dateRangeFilter]);

  // Debounced update cart function (500ms delay)
  const debouncedUpdateCart = useMemo(
    () =>
      debounce((cartItemId, quantity, authorId) => {
        if (onUpdateCart) {
          onUpdateCart({
            id: cartItemId,
            quantity,
            authorId,
          });
        }
      }, 500),
    [onUpdateCart]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateCart.cancel();
    };
  }, [debouncedUpdateCart]);

  // Memoize quantity change handler with debouncing
  const handleQuantityChange = useCallback(
    (listingId, newQuantity) => {
      const quantity = Math.max(1, newQuantity);

      // Update local state immediately for responsive UI
      setQuantities(prev => ({
        ...prev,
        [listingId]: quantity,
      }));

      // If item is in cart, debounce the cart update
      const cartItem = findCartItemForListing(
        listingId,
        dateRangeFilter,
        filteredCartItems
      );
      if (cartItem && onUpdateCart) {
        debouncedUpdateCart(
          cartItem.id,
          quantity,
          cartItem.authorId || cartItem.listing?.author?.id?.uuid
        );
      }
    },
    [
      dateRangeFilter,
      filteredCartItems,
      findCartItemForListing,
      debouncedUpdateCart,
    ]
  );

  // Helper function to get selected duration from date range
  // Calendar days between the two store-zone midnights, counted IN that zone - a
  // plain instant diff truncates to one day short across a spring-forward DST change
  // (23h + n days), which would pick the wrong price package and units.
  const getSelectedDuration = dateRangeFilter => {
    if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
      return null;
    }
    const inZone = d => (storeTimeZone ? moment(d).tz(storeTimeZone) : moment(d));
    return inZone(dateRangeFilter.endDate).diff(inZone(dateRangeFilter.startDate), 'days');
  };

  // Memoize add to cart handler
  const handleAddToCart = useCallback(
    (item, quantity) => {
      if (addToCartInProgress) {
        return;
      }

      // Validate date range is selected
      if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
        // Dates not selected, cannot add to cart
        return;
      }

      // Calculate duration in days
      const duration = getSelectedDuration(dateRangeFilter);
      if (!duration || duration < 1) {
        return;
      }

      // Determine selectedPackage based on duration (same logic as RentalCard)
      const durationMap = {
        1: 'perDayPrice',
        2: 'twoDaysPrice',
        3: 'threeDaysPrice',
        4: 'fourDaysPrice',
        5: 'fiveDaysPrice',
        7: 'oneWeekPrice',
        28: 'twentyEightDaysPrice',
      };
      const selectedPackage = durationMap[duration] || 'perDayPrice';

      // Get timeZone from store or fallback
      const timeZone = storeTimeZone;

      // Build complete cart params
      const cartParams = {
        listingId: item.objectID,
        authorId: item.user?.id,
        selectedPackage,
        bookingDates: {
          startDate: moment(dateRangeFilter.startDate).toISOString(),
          endDate: moment(dateRangeFilter.endDate).toISOString(),
        },
        timeZone,
        quantity: Number(quantity),
        units: duration, // Number of days
        usageMultipleValues: {}, // Can be populated from form if needed
        otherOptionalFees: [], // Can be populated from form if needed
        pickupAtOwnerLocation: false,
        returnAtOwnerLocation: false,
      };

      // Store profile "Add to Cart" is this page's equivalent of the listing page's
      // "Rent Now" - the explicit moment the user commits to a date range, not just
      // browses with one. Persisting it here (not on every dateRangeFilter change)
      // keeps the save tied to that commit action, same as BookingDatesForm.js/
      // BookingTimeForm.js do on the listing page.
      saveRentalDefaults(pickRentalDefaultsFromValues({ bookingDates: cartParams.bookingDates }));

      onAddToCart(cartParams);
    },
    [
      isCurrentUser,
      addToCartInProgress,
      onAddToCart,
      dateRangeFilter,
      storeTimeZone,
    ]
  );

  // How many skeleton cards to show while real data is loading. Matches the current
  // visibleCount (e.g. a restored deep-scroll count after browser back, RAS-2549) so the
  // page already reaches the right height before the network response arrives, instead
  // of sitting short at a fixed count until real cards replace the skeleton.
  const skeletonCount = Math.min(visibleCount, MAX_SKELETON_COUNT);

  // Initial load: show skeleton grid (no pagination)
  const isInitialLoading =
    (status === 'stalled' || status === 'loading') && items.length === 0;
  if (isInitialLoading) {
    const rentalsClassName =
      viewMode === 'list'
        ? `${css.rentals} ${css.rentalsList}`
        : `${css.rentals} ${css.rentalsGrid}`;
    return <StoreSkeletonGrid className={rentalsClassName} count={skeletonCount} />;
  }

  // Search in progress (query/category changed): show skeleton instead of stale results
  const isSearchRefreshing =
    (status === 'stalled' || status === 'loading') && items.length > 0;
  if (isSearchRefreshing) {
    const rentalsClassName =
      viewMode === 'list'
        ? `${css.rentals} ${css.rentalsList}`
        : `${css.rentals} ${css.rentalsGrid}`;
    return <StoreSkeletonGrid className={rentalsClassName} count={skeletonCount} />;
  }

  // Error state
  if (error) {
    return <H4>{error.message}</H4>;
  }

  // No results state
  if (!flattenedOrderedHits.length && status === 'idle') {
    return (
      <NoResult
        isCurrentUser={isCurrentUser}
        intl={intl}
        storeUserName={storeUserName}
        totalListings={totalListings}
      />
    );
  }

  // Render all items (no drag and drop, no modal)
  const rentalsClassName =
    viewMode === 'list'
      ? `${css.rentals} ${css.rentalsList}`
      : `${css.rentals} ${css.rentalsGrid}`;

  const handleLoadMore = () => {
    setVisibleCount(prev =>
      Math.min(prev + VISIBLE_INCREMENT, flattenedOrderedHits.length)
    );
  };

  return (
    <div className={css.sectionsWrapper}>
        {visibleSections.map((section, sectionIndex) => {
          const { sectionLabel, sectionPath, ancestors = [], hits: sectionHits, hideLabel } = section;
          const sectionKey = sectionPath || sectionLabel;
          const jumpKey = ancestors[0] || sectionLabel;

          return (
            <section
              key={`${sectionKey}-${sectionIndex}`}
              className={css.categorySection}
              aria-labelledby={hideLabel ? undefined : `category-section-heading-${sectionIndex}`}
              data-jump-key={jumpKey}
              ref={el => {
                if (el) sectionElsRef.current.set(sectionKey, el);
                else sectionElsRef.current.delete(sectionKey);
              }}
            >
              {/* Category header card: breadcrumb + title on left, count badge on right */}
              {!hideLabel && (
                <div className={css.categorySectionHeader}>
                  <div className={css.sectionHeaderLeft}>
                    {ancestors.length > 0 && (
                      <span className={css.sectionBreadcrumb}>
                        {ancestors.join(' › ')}
                      </span>
                    )}
                    <H1
                      id={`category-section-heading-${sectionIndex}`}
                      className={css.sectionHeadingH1}
                    >
                      {sectionLabel}
                    </H1>
                  </div>
                  <span className={css.sectionCount}>
                    {sectionTotalCounts.get(sectionKey) ?? sectionHits.length} Items
                  </span>
                </div>
              )}

              <div className={rentalsClassName}>
                {sectionHits.map(item => {
                  const cartItem = findCartItemForListing(
                    item.objectID,
                    dateRangeFilter,
                    filteredCartItems
                  );
                  const isInCart = !!cartItem;
                  const cartQuantity = cartItem?.quantity || 1;
                  const currentQuantity =
                    quantities[item.objectID] || cartQuantity;
                  const itemKey = cartItem
                    ? getItemOperationKey(
                      item.objectID,
                      dateRangeFilter,
                      cartItem.id
                    )
                    : getItemOperationKey(item.objectID, dateRangeFilter);
                  const itemOperation = itemOperationsInProgress[itemKey];
                  const isLoading = !!itemOperation;
                  const isAdding = itemOperation?.operation === 'add';
                  const isUpdating = itemOperation?.operation === 'update';
                  return (
                    <RentalCard
                      key={item.objectID}
                      rental={item}
                      dateRangeFilter={dateRangeFilter}
                      storeTimeZone={storeTimeZone}
                      quantity={currentQuantity}
                      onQuantityChange={handleQuantityChange}
                      onAddToCart={() => handleAddToCart(item, currentQuantity)}
                      availabilityStatus={availabilityStatus[item.objectID]}
                      availabilityLoading={availabilityLoading[item.objectID]}
                      isAdded={addedItems.includes(item.objectID)}
                      isInCart={isInCart}
                      cartItem={cartItem}
                      cartQuantity={cartQuantity}
                      isLoading={isLoading}
                      isAdding={isAdding}
                      isUpdating={isUpdating}
                      config={config}
                      exchangeRates={exchangeRates}
                      currentUserCurrency={currentUserCurrency}
                      isCurrentUser={isCurrentUser}
                      addToCartInProgress={addToCartInProgress}
                      viewMode={viewMode}
                      dateRangePickerRef={dateRangePickerRef}
                      datePickerAnchorRef={datePickerAnchorRef}
                      storePath={storePath}
                      intl={intl}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}

      {/* Sentinel for intersection observer: triggers load when ~70–80% scrolled */}
      {hasMoreVisible && (
        <div ref={sentinelRef} className={css.sentinel} aria-hidden="true" />
      )}

      {/* Fallback Load More button (client-side reveal only) */}
      {hasMoreVisible && (
        <div className={css.loadMoreWrapper}>
          <button
            type="button"
            className={css.loadMoreButton}
            onClick={handleLoadMore}
            disabled={!hasMoreVisible}
          >
            <FormattedMessage
              id="StoreProfilePage.CustomHits.loadMore"
              defaultMessage="Load More"
            />
          </button>
        </div>
      )}
    </div>
  );
};

CustomHits.propTypes = {
  intl: PropTypes.object.isRequired,
  sortOption: PropTypes.string.isRequired,
  storeUserName: PropTypes.string.isRequired,
  totalListings: PropTypes.number,
  config: PropTypes.object.isRequired,
  onAddToCart: PropTypes.func.isRequired,
  onUpdateCart: PropTypes.func,
  isCurrentUser: PropTypes.bool.isRequired,
  currentUser: PropTypes.object,
  exchangeRates: PropTypes.array,
  currentUserCurrency: PropTypes.string.isRequired,
  routes: PropTypes.array.isRequired,
  history: PropTypes.object.isRequired,
  dateRangeFilter: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
  }),
  params: PropTypes.shape({
    id: PropTypes.string,
  }),
  viewMode: PropTypes.oneOf(['grid', 'list']),
  onUpdateRecordsOrder: PropTypes.func,
  updateOrderError: PropTypes.object,
  onFetchTransactionLineItems: PropTypes.func,
  onFetchTimeSlots: PropTypes.func,
  storeTimeZone: PropTypes.string,
  filteredCartItems: PropTypes.array,
  datePickerAnchorRef: PropTypes.shape({ current: PropTypes.any }),
  storePath: PropTypes.string,
  user: PropTypes.object,
  selectedCategoryFilter: PropTypes.shape({
    categoryId: PropTypes.string,
    depth: PropTypes.number,
  }),
  onCategoryChange: PropTypes.func,
  searchQuery: PropTypes.string,
  pendingSearchSettledScrollRef: PropTypes.shape({ current: PropTypes.any }),
  onScrollToSearchSection: PropTypes.func,
};

export default CustomHits;
